/**
 * The small slice of Markdown the assistant is allowed to use.
 *
 * Model output is untrusted text, so this parses to React elements rather than HTML —
 * there is no `dangerouslySetInnerHTML` anywhere in the path, and no way for a reply to
 * introduce a tag, a link, or a script. It handles `**bold**`, `*italic*`, `` `code` ``
 * and hyphen bullets, which is everything the system prompt asks for; anything else
 * renders as the literal characters the model wrote.
 */

import { Fragment, type ReactNode } from "react";

/** `**a**`, `*a*`, `` `a` `` — leftmost-longest, so `**` wins over `*`. */
const INLINE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;

function inline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code key={key} className="num rounded bg-ink/[0.06] px-1 py-0.5 text-[0.9em]">
          {part.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

export function RichText({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];

  const flush = () => {
    if (bullets.length === 0) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="my-1 list-disc space-y-0.5 pl-4">
        {bullets.map((item, index) => (
          <li key={index}>{inline(item, `li-${blocks.length}-${index}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((line, index) => {
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      bullets.push(bullet[1] ?? "");
      return;
    }
    flush();
    if (line.trim() === "") {
      // Keep deliberate blank lines, but do not stack them.
      if (blocks.length > 0) blocks.push(<div key={`gap-${index}`} className="h-2" />);
      return;
    }
    blocks.push(<p key={`p-${index}`}>{inline(line, `p-${index}`)}</p>);
  });
  flush();

  return <>{blocks}</>;
}
